import RenameInterview from '@Application/Beneficiaries/Interviews/Commands/RenameInterview';
import type { InterviewRenameFailedAction } from '@Application/Beneficiaries/Interviews/Actions/interviewRenameFailed';
import renameInterview from '@Application/Beneficiaries/Interviews/renameInterview';
import { PermissionEnum } from '@Application/Security/Roles';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import EditableTitle from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Shared/EditableTitle';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import * as React from 'react';
import { useDispatch } from '@verclam/synapse';

type Props = {
	readonly subject: SubjectAddress;
	readonly interviewId: number;
	readonly title: string;
	/** The dossier accepts writes (not archived); the interview write permission is checked here. */
	readonly canWriteDossier: boolean;
};

/** The title of an interview or a note, renamable in place. Leaf component: it dispatches the rename itself. */
const InterviewTitle: React.FC<Props> = ({ subject, interviewId, title, canWriteDossier }) => {
	const dispatch = useDispatch();
	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;
	const canWriteInterview = useHasPermission(PermissionEnum.INTERVIEW_WRITE);

	const handleRename = React.useCallback(
		(name: string) =>
			dispatch(renameInterview(new RenameInterview(facilityId, subject, interviewId, name))).then(
				() => undefined,
				(failure: InterviewRenameFailedAction) => Promise.reject(failure.payload),
			),
		[dispatch, facilityId, subject, interviewId],
	);

	return (
		<EditableTitle
			title={title}
			editKey={`interview-${interviewId}`}
			canRename={canWriteDossier && canWriteInterview}
			fieldLabel="Nom de l'entretien"
			onRename={handleRename}
		/>
	);
};

export default InterviewTitle;
